/**
 * IntakeBundlePrefillDialog
 * ─────────────────────────
 * Mar 2026 — Provider workflow: fill in bundle documents BEFORE
 * sending them to the client, so they don't have to invalidate a
 * signature later just to add a missing name / date on a consent
 * form.
 *
 * Open flow:
 *   1. Provider clicks "Send Intake Bundle" in AppointmentScheduleDialog.
 *   2. Instead of sending immediately, we call the send-intake-bundle
 *      endpoint with `prepare_only=true` — creates the bundle notes
 *      in ``draft`` state, no email to client yet.
 *   3. This dialog opens, fetches each note's editable fields via
 *      GET /clients/{client_id}/intake-bundle/{bundle_id}/notes.
 *   4. Provider fills in / edits any fields they want.  Each doc is a
 *      collapsible card; docs that have provider-fillable fields
 *      auto-expand.
 *   5. Provider clicks either:
 *      - "Save Draft" — closes dialog, notes stay in draft (client
 *        never notified).  Provider can reopen later from Pending
 *        Send list.
 *      - "Send to Client" — validates required fields, saves any
 *        edits, then triggers the consolidated client email.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight, FileText, Send, Save, Loader2, Info, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A field is provider-editable when it's NOT a pure signature/date
// stamp meant for the client.  We render every non-signature field
// so either party can fill or edit (per user's Mar 2026 requirement).
const CLIENT_ONLY_FIELD_TYPES = new Set([
  'client_signature',
  'signature',
  'signature_pad',
  'client_initial',
  'initials',
]);

const isEditableForProvider = (field) => {
  const t = (field?.type || '').toLowerCase();
  if (CLIENT_ONLY_FIELD_TYPES.has(t)) return false;
  // Static display types have no `value` to edit.
  if (['heading', 'divider', 'spacer', 'static_text', 'html'].includes(t)) return false;
  return true;
};

const hasProviderFillableFields = (note) => {
  const fields = _extractFields(note);
  return fields.some(isEditableForProvider);
};

// Notes store content as a JSON string in ``note.content``.  Fields
// live at either ``content.fields`` OR at the top level in ``content``
// depending on template age.  This normalises to an array of
// ``{ id/key, label, type, value }`` entries the dialog can iterate.
function _extractFields(note) {
  if (!note) return [];
  let content = note.content;
  if (typeof content === 'string') {
    try { content = JSON.parse(content); } catch { content = {}; }
  }
  if (Array.isArray(content?.fields)) return content.fields;
  // Fallback — legacy templates
  const tmpl = note._template || {};
  return Array.isArray(tmpl.form_fields) ? tmpl.form_fields : [];
}

function _writeFields(note, updatedFields) {
  let content = note.content;
  if (typeof content === 'string') {
    try { content = JSON.parse(content); } catch { content = {}; }
  }
  content = content || {};
  content.fields = updatedFields;
  return { ...note, content: JSON.stringify(content) };
}

export const IntakeBundlePrefillDialog = ({
  open,
  onClose,
  appointmentId,
  clientId,
  bundleId,
  bundleName,
  onSent,
}) => {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  // Load bundle notes when opening.
  const load = useCallback(async () => {
    if (!clientId || !bundleId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/clients/${clientId}/intake-bundle/${bundleId}/notes`,
      );
      const rows = data?.notes || [];
      setNotes(rows);
      // Auto-expand any note that has provider-fillable fields —
      // saves the provider a click on the common case.
      const expand = new Set();
      rows.forEach((n) => {
        if (hasProviderFillableFields(n)) expand.add(n.id);
      });
      setExpandedIds(expand);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load bundle documents');
    } finally {
      setLoading(false);
    }
  }, [clientId, bundleId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateFieldValue = (noteId, fieldIdx, newValue) => {
    setNotes((prev) => prev.map((n) => {
      if (n.id !== noteId) return n;
      const fields = _extractFields(n).slice();
      fields[fieldIdx] = { ...fields[fieldIdx], value: newValue };
      return _writeFields(n, fields);
    }));
  };

  const saveAllEdits = useCallback(async () => {
    setSaving(true);
    try {
      // Persist every note.  The individual notes PATCH endpoint
      // accepts a content-only body — we send that back verbatim.
      // Mar 2026: dedupe on ``id`` in case anything double-mounted.
      const seen = new Set();
      const jobs = [];
      for (const n of notes) {
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        jobs.push(axios.put(`${API}/notes/${n.id}`, { content: n.content }));
      }
      await Promise.all(jobs);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save one or more documents');
      return false;
    } finally {
      setSaving(false);
    }
  }, [notes]);

  const handleSaveDraft = async () => {
    const ok = await saveAllEdits();
    if (ok) {
      toast.success('Saved as draft. Client has NOT been notified yet.');
      if (onSent) onSent({ sent: false, saved: true });
      onClose?.();
    }
  };

  const handleSendToClient = async () => {
    const saveOk = await saveAllEdits();
    if (!saveOk) return;
    setSending(true);
    try {
      // Jun 2026 — chart-based sends have no appointment; use the
      // client-scoped endpoint. Appointment-linked sends keep the
      // appointment endpoint so the appointment gets stamped.
      const url = appointmentId
        ? `${API}/appointments/${appointmentId}/send-intake-bundle`
        : `${API}/clients/${clientId}/send-intake-bundle`;
      const { data } = await axios.post(url, { bundle_id: bundleId });
      toast.success(`Sent ${data?.documents_sent ?? notes.length} document(s) to client in one email`);
      if (onSent) onSent({ sent: true, ...data });
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send to client');
    } finally {
      setSending(false);
    }
  };

  const totalDocs = notes.length;
  const docsWithProviderFields = useMemo(
    () => notes.filter(hasProviderFillableFields).length,
    [notes],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && !saving && onClose?.()}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="intake-bundle-prefill-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Review &amp; Fill: {bundleName || 'Intake Bundle'}
          </DialogTitle>
          <div className="flex items-start gap-2 text-sm text-gray-600 mt-2 bg-blue-50 border border-blue-200 rounded p-2">
            <Info className="h-4 w-4 mt-0.5 text-blue-600 shrink-0" />
            <span>
              Fill in any fields the client shouldn&apos;t need to complete themselves
              (e.g. &ldquo;consent is for {'{name}'}&rdquo;).  Client signature fields are hidden
              here — the client will sign them via the portal.  When you&apos;re done,
              click <strong>Send to Client</strong> to email the bundle in one message.
            </span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-3 px-1">
          {loading && (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading documents…
            </div>
          )}
          {!loading && totalDocs === 0 && (
            <div className="text-center py-10 text-sm text-gray-500">
              No documents in this bundle yet.
            </div>
          )}
          {!loading && notes.map((n) => (
            <PrefillNoteCard
              key={n.id}
              note={n}
              expanded={expandedIds.has(n.id)}
              onToggle={() => toggleExpanded(n.id)}
              onFieldChange={(idx, val) => updateFieldValue(n.id, idx, val)}
            />
          ))}
        </div>

        <DialogFooter className="border-t pt-3 flex-shrink-0">
          <div className="text-xs text-gray-500 mr-auto self-center">
            {totalDocs} document{totalDocs === 1 ? '' : 's'} in bundle
            {docsWithProviderFields > 0 && (
              <span className="ml-1">
                · {docsWithProviderFields} with provider-fillable fields
              </span>
            )}
          </div>
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={saving || sending || loading}
            data-testid="prefill-save-draft-btn"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button
            onClick={handleSendToClient}
            disabled={saving || sending || loading || totalDocs === 0}
            data-testid="prefill-send-to-client-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? 'Sending…' : 'Send to Client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const PrefillNoteCard = ({ note, expanded, onToggle, onFieldChange }) => {
  const fields = _extractFields(note);
  const hasProviderFields = fields.some(isEditableForProvider);
  const templateName = note.template_name || note._template?.name || 'Untitled document';

  return (
    <Card data-testid={`prefill-note-card-${note.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left"
        data-testid={`prefill-note-toggle-${note.id}`}
      >
        <CardHeader className="flex-row items-center justify-between space-y-0 py-2 px-4">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="text-sm font-semibold">{templateName}</span>
            {note.doc_number && (
              <span className="text-[10px] font-mono text-gray-500">{note.doc_number}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {hasProviderFields ? (
              <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 bg-amber-50">
                prefill available
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] border-emerald-400 text-emerald-700 bg-emerald-50">
                <Check className="h-3 w-3 mr-0.5" /> client-only
              </Badge>
            )}
          </div>
        </CardHeader>
      </button>
      {expanded && (
        <CardContent className="pt-0 pb-3 px-4 space-y-3">
          {fields.length === 0 && (
            <p className="text-xs text-gray-500">No editable fields in this document.</p>
          )}
          {fields.map((f, idx) => {
            if (!isEditableForProvider(f)) return null;
            return (
              <FieldEditor
                key={f.id || f.key || `${note.id}-${idx}`}
                field={f}
                onChange={(v) => onFieldChange(idx, v)}
                testIdPrefix={`prefill-${note.id}-${idx}`}
              />
            );
          })}
          {fields.length > 0 && !hasProviderFields && (
            <p className="text-xs text-gray-500">
              This document is client-only — nothing to fill on your end.
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
};


const FieldEditor = ({ field, onChange, testIdPrefix }) => {
const FIELD_TYPE_LABELS = {
  client_name: 'Client Name',
  client_dob: 'Date of Birth',
  mrn: 'MRN',
  provider_name: 'Provider Name',
  service_date: 'Date of Service',
  date: 'Date',
  phone: 'Phone',
  email: 'Email',
};

  const label = field.label || field.name || FIELD_TYPE_LABELS[field.type] || 'Additional Information';
  const type = (field.type || '').toLowerCase();
  const value = field.value ?? '';

  if (type === 'textarea' || type === 'multiline' || type === 'long_text') {
    return (
      <div>
        <Label className="text-xs">{label}{field.required && <span className="text-rose-600 ml-1">*</span>}</Label>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="text-sm"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>
    );
  }

  if (type === 'checkbox' || type === 'boolean') {
    return (
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`${testIdPrefix}-input`}
        />
        <Label className="text-xs">{label}</Label>
      </div>
    );
  }

  if (type === 'select' || type === 'dropdown') {
    const opts = Array.isArray(field.options) ? field.options : [];
    return (
      <div>
        <Label className="text-xs">{label}{field.required && <span className="text-rose-600 ml-1">*</span>}</Label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-8 rounded border border-gray-300 text-sm px-2"
          data-testid={`${testIdPrefix}-input`}
        >
          <option value="">— select —</option>
          {opts.map((o, i) => (
            <option key={i} value={typeof o === 'string' ? o : (o.value ?? o.label)}>
              {typeof o === 'string' ? o : (o.label ?? o.value)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // date / text / number / email / phone / anything else → plain input
  const inputType = ['date', 'number', 'email', 'tel', 'phone'].includes(type)
    ? (type === 'phone' ? 'tel' : type)
    : 'text';

  return (
    <div>
      <Label className="text-xs">{label}{field.required && <span className="text-rose-600 ml-1">*</span>}</Label>
      <Input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-sm"
        data-testid={`${testIdPrefix}-input`}
      />
    </div>
  );
};

export default IntakeBundlePrefillDialog;
